/**
 * The first-visit tip (UI-022): one line over the table saying what to do, for a
 * device that has never played, gone at its first shot.
 *
 * "Never played" is read from the history the device already keeps (DLY-004), plus one
 * flag of its own for what history does not hold: a shot on a past table, or the tip
 * closed with its ✕. Once either happens it never shows again on this device.
 */
import { COUNTS, FIRST_VISIT_HINT } from '../config/site.ts';

/** Set to "1" once this device has shot or closed the tip. Nothing else is stored. */
export const HINT_SEEN_KEY = 'potted.tip-seen.v1';

export interface HintContext {
  /** Days in this device's history. */
  readonly daysPlayed: number;
  /** `HINT_SEEN_KEY` is set on this device. */
  readonly seen: boolean;
  /** Shots already taken on the table being shown (a resumed run has some). */
  readonly shotsTaken: number;
  /** The debug lab is for the owner, who does not need telling. */
  readonly debug: boolean;
  readonly ballCount: number;
  readonly par: number | undefined;
}

/** The tip's two lines, or undefined when this player does not need it. */
export function firstVisitHint(context: HintContext): readonly [string, string] | undefined {
  if (context.debug || context.seen || context.daysPlayed > 0 || context.shotsTaken > 0) {
    return undefined;
  }
  const balls = context.ballCount === 1 ? COUNTS.ball[0] : COUNTS.ball[1];
  const goal = `Pot all ${String(context.ballCount)} ${balls} in as few shots as you can.`;
  return [
    context.par === undefined ? goal : `${goal} ${FIRST_VISIT_HINT.par(context.par)}`,
    FIRST_VISIT_HINT.aim,
  ];
}

/**
 * Show the tip just above the table, in the page's flow, so it never covers a ball: the
 * table gives up the room while it is there. Returns a function that removes it;
 * `onGone` runs once it is gone, by that function or by its ✕.
 */
export function mountHint(
  table: Element,
  lines: readonly [string, string],
  onGone: () => void,
): () => void {
  const hint = document.createElement('div');
  hint.className = 'hint';
  hint.setAttribute('role', 'status');
  const text = document.createElement('p');
  text.className = 'hint__text';
  const goal = document.createElement('strong');
  goal.textContent = lines[0];
  text.append(goal, document.createElement('br'), lines[1]);
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'btn btn--icon hint__close';
  close.setAttribute('aria-label', FIRST_VISIT_HINT.dismiss);
  close.textContent = '×';
  hint.append(text, close);
  table.before(hint);
  const remove = (): void => {
    if (!hint.isConnected) return;
    hint.remove();
    onGone();
  };
  close.addEventListener('click', remove);
  return remove;
}
